import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Section, Field, ActionBar } from "@/components/form-shell";

const SUBJECT_OPTIONS = [
  { value: "Deal",             label: "Deal" },
  { value: "Submission",       label: "Submission" },
  { value: "GovOpportunity",   label: "Government Opportunity" },
  { value: "Account",          label: "Account" },
  { value: "Other",            label: "Other (free-form)" },
];

export default function MyRequestNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    type: "DISCOUNT",
    subjectType: "Deal",
    subjectId: "",
    subjectLabel: "",                       // shown only when "Other"
    approverId: "",
    rationale: "",
    amount: "",
  });

  const users = useQuery({
    queryKey: ["/users"],
    queryFn: async () => (await api.get("/users")).data.data as { id: string; name: string; role: string }[],
  });

  // Only show admins / leadership / finance / staffing managers as potential approvers
  const approvers = useMemo(
    () => (users.data ?? []).filter((u) => ["SUPER_ADMIN", "ADMIN", "LEADERSHIP", "FINANCE", "STAFFING_MANAGER", "SALES_MANAGER"].includes(u.role)),
    [users.data],
  );

  // When subjectType is a known entity, fetch matching records for the picker.
  const [subjectOptions, setSubjectOptions] = useState<{ id: string; label: string }[]>([]);
  useEffect(() => {
    if (form.subjectType === "Other") { setSubjectOptions([]); return; }
    const endpoint =
      form.subjectType === "Deal"           ? "/deals?pageSize=50" :
      form.subjectType === "Submission"     ? "/submissions" :
      form.subjectType === "GovOpportunity" ? "/gov-opportunities?pageSize=50" :
      form.subjectType === "Account"        ? "/accounts?pageSize=50" : null;
    if (!endpoint) return;
    api.get(endpoint).then((r) => {
      const rows = r.data.data ?? [];
      setSubjectOptions(
        rows.map((x: any) => ({
          id: x.id,
          label: x.name ?? x.title ?? x.id.slice(0, 8),
        })),
      );
    }).catch(() => setSubjectOptions([]));
  }, [form.subjectType]);

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  const mut = useMutation({
    mutationFn: async () => {
      const payload: any = {
        type: form.type,
        subjectType: form.subjectType,
        subjectId: form.subjectType === "Other" ? `free-form:${form.subjectLabel}` : form.subjectId,
        rationale: form.rationale,
        requestPayload: form.amount ? { amount: Number(form.amount) } : undefined,
      };
      if (form.approverId) payload.approverId = form.approverId;
      await api.post("/approvals", payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/approvals"] });
      navigate("/my-requests");
    },
    onError: (err: any) => setError(err?.response?.data?.message ?? "Failed to submit request."),
  });

  async function save() {
    setBusy(true); setError(null);
    try { await mut.mutateAsync(); } finally { setBusy(false); }
  }

  const canSubmit = !!form.type && !!form.rationale && (form.subjectType === "Other" ? !!form.subjectLabel : !!form.subjectId);

  return (
    <>
      <PageHeader title="New Request" description="Ask for approval — discount, bid/no-bid, expense, or other." />
      <form onSubmit={(e) => { e.preventDefault(); save(); }} className="p-6">
        <div className="mx-auto max-w-2xl space-y-4">
          <Section title="Request">
            <Field id="type" label="Type" required>
              <Select id="type" value={form.type} onChange={(e) => set("type", e.target.value)}>
                <option value="DISCOUNT">Discount approval</option>
                <option value="BID_NO_BID">Bid / No-bid</option>
                <option value="SUBMISSION">Submission / candidate</option>
                <option value="EXPENSE">Expense</option>
                <option value="OTHER">Other</option>
              </Select>
            </Field>
            <Field id="amount" label="Amount ($) — optional">
              <Input id="amount" type="number" min="0" value={form.amount} onChange={(e) => set("amount", e.target.value)} />
            </Field>
            <Field id="subjectType" label="Related to" required>
              <Select id="subjectType" value={form.subjectType} onChange={(e) => set("subjectType", e.target.value)}>
                {SUBJECT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            </Field>
            {form.subjectType === "Other" ? (
              <Field id="subjectLabel" label="Subject" required>
                <Input id="subjectLabel" value={form.subjectLabel} onChange={(e) => set("subjectLabel", e.target.value)} placeholder="Short label" />
              </Field>
            ) : (
              <Field id="subjectId" label="Record" required>
                <Select id="subjectId" value={form.subjectId} onChange={(e) => set("subjectId", e.target.value)}>
                  <option value="">— Select record —</option>
                  {subjectOptions.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </Select>
              </Field>
            )}
            <Field id="approverId" label="Approver (optional)">
              <Select id="approverId" value={form.approverId} onChange={(e) => set("approverId", e.target.value)}>
                <option value="">— Any admin / manager —</option>
                {approvers.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role.replace(/_/g, " ")})</option>)}
              </Select>
            </Field>
            <div className="md:col-span-2">
              <label htmlFor="rationale" className="text-sm font-medium leading-none">Rationale *</label>
              <textarea
                id="rationale"
                required
                value={form.rationale}
                onChange={(e) => set("rationale", e.target.value)}
                className="mt-1 flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Why do you need this approval? Context, numbers, expected impact…"
              />
            </div>
          </Section>

          <ActionBar
            busy={busy}
            canSubmit={canSubmit}
            onCancel={() => navigate("/my-requests")}
            onSubmit={save}
            error={error}
          />
        </div>
      </form>
    </>
  );
}
